import { Component, Input, OnInit } from '@angular/core';
import {
  DiagnosticResult,
  DiagnosticDetails,
  DiagnosticFinding,
  FillStatus,
  ImpressionSummaryRow,
  Step5Data,
  Step6Data
} from '../../models/diagnostic.models';

interface StepEntry {
  key: string;
  data: any;
  exitedHere: boolean;
  defaultOpen: boolean;
}

@Component({
  selector: 'app-early-exit-card',
  templateUrl: './early-exit-card.component.html',
  styleUrls: ['./early-exit-card.component.scss']
})
export class EarlyExitCardComponent implements OnInit {
  readonly String = String;
  @Input() data: DiagnosticResult = {} as DiagnosticResult;

  stepEntries: StepEntry[] = [];
  otherEntries: [string, any][] = [];
  isCompletion = false;
  findings: DiagnosticFinding[] = [];
  openSteps: Record<string, boolean> = {};
  showStoresMap: Record<string, boolean> = {};
  showDetailsMap: Record<string, boolean> = {};

  readonly stepTitles: Record<number, string> = {
    0: 'Paso 0 · Estado',
    1: 'Paso 1 · Budget',
    2: 'Paso 2 · Ads escritos',
    3: 'Paso 3 · Combinaciones',
    4: 'Paso 4 · Keywords',
    5: 'Paso 5 · Stock'
  };

  readonly statusLabels: Record<FillStatus, string> = {
    delivering: 'Delivering',
    exhausted: 'Exhausted',
    no_delivery: 'No delivery',
    no_data: 'No data'
  };

  readonly keyLabels: Record<string, string> = {
    campaign_status: 'Status', start_date: 'Inicio', end_date: 'Fin',
    budget: 'Budget', spend_data: 'Spend (data)', spend_ad_server: 'Spend (ad server)',
    remaining_budget: 'Remaining', ads_written: 'Ads escritos hoy', total_rows: 'Filas (defrag)',
    total_combinations: 'Total combinaciones', total_categories: 'Total categorías',
    total_products: 'Total productos', products_with_stock: 'Productos con stock',
    products_without_stock: 'Productos sin stock', categorie_id: 'Categoría ID',
    category_name: 'Nombre', keywords: 'Keywords', product_id: 'Product ID',
    in_stock_stores: 'Tiendas con stock', total_stores_with_stock: 'Tiendas con stock',
    stock_rank: '#', store_rank: '#', store_id: 'Store ID', super_store_id: 'Super Store ID',
    store_type: 'Store type', store_name: 'Tienda', store_address: 'Dirección',
    stock: 'Stock', products_in_store: 'Productos en tienda', campaign_id: 'Campaign ID',
    placement: 'Placement', ad_slot: 'Ad slot', renders: 'Renders',
    renders_planned: 'Planificados', renders_delivered: 'Entregados',
    fill_rate: 'Fill rate', fill_status: 'Estado', ad_slots: 'Ad slots',
    total_pairs: 'Total combinaciones', total_renders_planned: 'Total planificados',
    total_renders_delivered: 'Total entregados', overall_fill_rate: 'Fill rate global'
  };

  private readonly moneyKeys = new Set([
    'budget', 'spend_data', 'spend_ad_server', 'remaining_budget'
  ]);

  ngOnInit(): void {
    const nested = this.data?.details || {};
    const hasNestedSteps = Object.keys(nested).some(k => /^step\d+$/.test(k));
    const details: DiagnosticDetails = hasNestedSteps ? nested : this.extractRootSteps(this.data);

    this.isCompletion = this.data?.status === 'completed' ||
      (this.data?.exit_step ?? 0) >= 5 ||
      /completad/i.test(this.data?.exit_reason || '');

    const stepKeys = Object.keys(details).filter(k => /^step\d+$/.test(k));
    const otherKeys = Object.keys(details).filter(k => !/^step\d+$/.test(k));

    this.stepEntries = stepKeys.map(key => {
      const hasLargeTable = key !== 'step5' && Object.values(details[key] || {}).some(
        v => Array.isArray(v) && v.length > 10
      );
      return {
        key,
        data: details[key],
        exitedHere: !this.isCompletion && Number(key.replace('step', '')) === this.data?.exit_step,
        defaultOpen: !hasLargeTable
      };
    });

    this.stepEntries.forEach(e => {
      this.openSteps[e.key] = e.defaultOpen;
    });
    this.otherEntries = otherKeys.map(k => [k, details[k]]);
    this.findings = this.buildFindings(details);
  }

  safe(val: any, fallback = '—'): any {
    return val !== null && val !== undefined ? val : fallback;
  }

  toggleStep(key: string): void {
    this.openSteps[key] = !this.openSteps[key];
  }

  toggleStores(key: string): void {
    this.showStoresMap[key] = !this.showStoresMap[key];
  }

  toggleDetails(key: string): void {
    this.showDetailsMap[key] = !this.showDetailsMap[key];
  }

  labelFor(key: string): string {
    return this.keyLabels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  formatValue(key: string, value: any): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    if (typeof value === 'boolean') {
      return value ? 'Sí' : 'No';
    }
    if (this.moneyKeys.has(key) && typeof value === 'number') {
      return this.formatMoney(value);
    }
    if (Array.isArray(value)) {
      return value.length === 0 ? '—' : value.join(', ');
    }
    return String(value);
  }

  formatMoney(value: number): string {
    if (value === null || value === undefined || isNaN(value)) {
      return '—';
    }
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);
  }

  formatNumber(value: number): string {
    if (value === null || value === undefined || isNaN(value)) {
      return '—';
    }
    return new Intl.NumberFormat('es-CO').format(value);
  }

  formatFillRate(value: number | null): string {
    if (value === null || value === undefined) {
      return '—';
    }
    return `${(Math.min(100, Math.max(0, value * 100))).toFixed(1)}%`;
  }

  isPrimitive(value: any): boolean {
    return !Array.isArray(value) && !(value && typeof value === 'object');
  }

  isArrayOfObjects(value: any): boolean {
    return Array.isArray(value) && value.length > 0 && value.every(it => it && typeof it === 'object' && !Array.isArray(it));
  }

  isPlainObject(value: any): boolean {
    return value && typeof value === 'object' && !Array.isArray(value);
  }

  primitiveEntries(data: any): [string, any][] {
    return Object.entries(data || {}).filter(([, v]) => this.isPrimitive(v));
  }

  tableEntries(data: any): [string, any[]][] {
    return Object.entries(data || {}).filter(([, v]) => this.isArrayOfObjects(v)) as [string, any[]][];
  }

  objectEntries(data: any): [string, any][] {
    return Object.entries(data || {}).filter(([, v]) => this.isPlainObject(v) && !this.isArrayOfObjects(v));
  }

  tableColumns(rows: any[]): string[] {
    const cols = new Set<string>();
    rows.forEach(r => Object.keys(r).forEach(k => cols.add(k)));
    return Array.from(cols);
  }

  step1BudgetPct(data: any): number {
    const budget = data?.budget || 0;
    const spend = Math.max(data?.spend_data || 0, data?.spend_ad_server || 0);
    return budget > 0 ? Math.min(100, (spend / budget) * 100) : 0;
  }

  step1BarColor(pct: number): string {
    if (pct >= 100) {
      return '#f43f5e';
    }
    if (pct >= 80) {
      return '#f59e0b';
    }
    return '#10b981';
  }

  step0StatusTone(data: any): string {
    const s = (data?.campaign_status || '').toLowerCase();
    if (s === 'active') {
      return 'emerald';
    }
    if (s === 'paused') {
      return 'amber';
    }
    return 'rose';
  }

  fillRateBarColor(value: number | null): string {
    if (value === null || value === undefined) {
      return '#94a3b8';
    }
    if (value >= 1) {
      return '#f59e0b';
    }
    if (value === 0) {
      return '#f43f5e';
    }
    return '#10b981';
  }

  fillRatePct(value: number | null): number {
    if (value === null || value === undefined) {
      return 0;
    }
    return Math.min(100, Math.max(0, value * 100));
  }

  fillStatusClass(status: FillStatus): string {
    const map: Record<FillStatus, string> = {
      delivering: 'eec-badge--emerald',
      exhausted: 'eec-badge--amber',
      no_delivery: 'eec-badge--rose',
      no_data: 'eec-badge--slate'
    };
    return map[status] || 'eec-badge--slate';
  }

  fillStatusDot(status: FillStatus): string {
    const map: Record<FillStatus, string> = {
      delivering: '#10b981',
      exhausted: '#f59e0b',
      no_delivery: '#f43f5e',
      no_data: '#94a3b8'
    };
    return map[status] || '#94a3b8';
  }

  step6OverallStatus(data: Step6Data): FillStatus {
    const fr = data?.overall_fill_rate;
    if (fr === null || fr === undefined) {
      return 'no_data';
    }
    if (fr >= 1) {
      return 'exhausted';
    }
    if (fr === 0) {
      return 'no_delivery';
    }
    return 'delivering';
  }

  step6Headline(data: Step6Data): string {
    const s = this.step6OverallStatus(data);
    if (s === 'no_delivery') {
      return 'Sin impresiones entregadas';
    }
    if (s === 'exhausted') {
      return 'Inventario completado';
    }
    if (s === 'no_data') {
      return 'Sin datos disponibles';
    }
    return 'Entrega en curso';
  }

  step6SortedSummary(data: Step6Data): ImpressionSummaryRow[] {
    const statusOrder: Record<FillStatus, number> = { no_delivery: 0, no_data: 1, exhausted: 2, delivering: 3 };
    return [...(data?.summary || [])].sort((a, b) => {
      const oa = statusOrder[a.fill_status] ?? 9;
      const ob = statusOrder[b.fill_status] ?? 9;
      return oa !== ob ? oa - ob : (a.fill_rate ?? 0) - (b.fill_rate ?? 0);
    });
  }

  step6NoDeliveryCount(data: Step6Data): number {
    return (data?.summary || []).filter(s => s.fill_status === 'no_delivery').length;
  }

  step5HasMatch(data: Step5Data): boolean {
    const target = data?.url_store_id;
    if (!target) {
      return false;
    }
    return (data?.rows || []).some(r => String(r.store_id) === String(target));
  }

  donutPath(value: number | null, size: number): string {
    const stroke = 14;
    const radius = (size - stroke) / 2;
    const c = 2 * Math.PI * radius;
    const pct = value === null || value === undefined ? 0 : Math.min(100, Math.max(0, value * 100));
    const offset = c - (pct / 100) * c;
    return `${c} ${offset}`;
  }

  donutColor(value: number | null): string {
    return this.fillRateBarColor(value);
  }

  donutDisplay(value: number | null): string {
    return this.formatFillRate(value);
  }

  donutRadius(size: number): number {
    return (size - 14) / 2;
  }

  donutCircumference(size: number): number {
    return 2 * Math.PI * this.donutRadius(size);
  }

  donutOffset(value: number | null, size: number): number {
    const c = this.donutCircumference(size);
    const pct = value === null || value === undefined ? 0 : Math.min(100, Math.max(0, value * 100));
    return c - (pct / 100) * c;
  }

  severity(): 'ok' | 'warning' | 'critical' {
    const criticals = this.findings.filter(f => f.severity === 'critical').length;
    const warnings = this.findings.filter(f => f.severity === 'warning').length;
    if (criticals > 0) {
      return 'critical';
    }
    if (warnings > 0) {
      return 'warning';
    }
    return 'ok';
  }

  completionHeadline(): string {
    const criticals = this.findings.filter(f => f.severity === 'critical').length;
    const warnings = this.findings.filter(f => f.severity === 'warning').length;
    if (criticals > 0) {
      return `${criticals} problema${criticals === 1 ? '' : 's'} crítico${criticals === 1 ? '' : 's'} detectado${criticals === 1 ? '' : 's'}.`;
    }
    if (warnings > 0) {
      return `${warnings} observación${warnings === 1 ? '' : 'es'} en el diagnóstico.`;
    }
    return 'La campaña pasa todos los chequeos del diagnóstico.';
  }

  findingsByType(sev: string): DiagnosticFinding[] {
    return this.findings.filter(f => f.severity === sev);
  }

  findingDotColor(sev: string): string {
    if (sev === 'ok') {
      return '#10b981';
    }
    if (sev === 'warning') {
      return '#f59e0b';
    }
    return '#f43f5e';
  }

  stepConfigFor(key: string): { title: string; subtitle: string; accent: string; hideStepNumber?: boolean } {
    const map: Record<string, { title: string; subtitle: string; accent: string; hideStepNumber?: boolean }> = {
      step0: { title: 'Estado', subtitle: 'Status de la campaña', accent: 'indigo' },
      step1: { title: 'Budget', subtitle: 'Presupuesto y gasto', accent: 'emerald' },
      step2: { title: 'Ads escritos', subtitle: 'Verificación en defrag', accent: 'sky' },
      step3: { title: 'Combinaciones', subtitle: 'Placement × Store type × Categoría', accent: 'violet' },
      step4: { title: 'Keywords', subtitle: 'Palabras del cloud por categoría', accent: 'amber' },
      step5: { title: 'Stock', subtitle: 'Disponibilidad por tienda', accent: 'rose' },
      step6: { title: 'Impresiones', subtitle: 'Renders planificados vs entregados hoy', accent: 'teal', hideStepNumber: true },
      step7: { title: 'Sugerencias · Dónde sí está entregando', subtitle: '', accent: 'violet', hideStepNumber: true }
    };
    return map[key] || { title: key, subtitle: '', accent: 'slate' };
  }

  stepAccentClass(accent: string): string {
    const map: Record<string, string> = {
      indigo: 'eec-accent--indigo', emerald: 'eec-accent--emerald', sky: 'eec-accent--sky',
      violet: 'eec-accent--violet', amber: 'eec-accent--amber', rose: 'eec-accent--rose',
      teal: 'eec-accent--teal', slate: 'eec-accent--slate'
    };
    return map[accent] || 'eec-accent--slate';
  }

  stepNumber(key: string): number {
    return Number(key.replace('step', ''));
  }

  private extractRootSteps(data: any): DiagnosticDetails {
    const result: DiagnosticDetails = {};
    Object.keys(data || {}).forEach(k => {
      if (/^step\d+$/.test(k)) {
        result[k] = data[k];
      }
    });
    return result;
  }

  private buildStep0Finding(step0: any): DiagnosticFinding | null {
    const status = (step0.campaign_status || '').toLowerCase();
    if (status === 'active') {
      return { severity: 'ok', text: `Campaña activa (vigente del ${step0.start_date} al ${step0.end_date}).` };
    }
    return null;
  }

  private buildStep1Finding(step1: any): DiagnosticFinding | null {
    const budget = step1.budget;
    const remaining = step1.remaining_budget;
    if (typeof budget !== 'number' || typeof remaining !== 'number') {
      return null;
    }
    const pctLeft = budget > 0 ? (remaining / budget) * 100 : 0;
    if (remaining <= 0) {
      return { severity: 'critical', text: 'Budget agotado — la campaña no tiene presupuesto restante.' };
    }
    if (pctLeft < 10) {
      return {
        severity: 'warning',
        text: `Budget casi agotado: ${this.formatMoney(remaining)} restante (${pctLeft.toFixed(1)}% del total).`
      };
    }
    return {
      severity: 'ok',
      text: `Budget disponible: ${this.formatMoney(remaining)} restante de ${this.formatMoney(budget)}.`
    };
  }

  private buildStep2Finding(step2: any): DiagnosticFinding | null {
    if (step2.ads_written) {
      return { severity: 'ok', text: `${this.formatNumber(step2.total_rows)} filas escritas hoy en la tabla defrag.` };
    }
    if ('ads_written' in step2 && step2.ads_written === false) {
      return { severity: 'critical', text: 'No hay ads escritos hoy en la tabla defrag para esta campaña.' };
    }
    return null;
  }

  private buildStep3Finding(step3: any, hasStep3: boolean): DiagnosticFinding | null {
    if (!hasStep3) {
      return null;
    }
    const totalCombos = step3.total_combinations || 0;
    if (totalCombos === 0) {
      return { severity: 'critical', text: 'No hay combinaciones placement × store type configuradas para este store type.' };
    }
    return { severity: 'ok', text: `${totalCombos} combinaciones placement × ad slot × categoría configuradas.` };
  }

  private buildStep4Finding(step4: any, hasStep4: boolean): DiagnosticFinding | null {
    if (!hasStep4 || !step4.search_keyword) {
      return null;
    }
    const matches = step4.categories_with_match || 0;
    const totalCats = step4.total_categories || 0;
    if (matches === 0 && totalCats > 0) {
      return {
        severity: 'warning',
        text: `La keyword "${step4.search_keyword}" no aparece en las palabras del cloud de ninguna de las ${totalCats} categorías.`
      };
    }
    if (matches > 0) {
      return {
        severity: 'ok',
        text: `La keyword "${step4.search_keyword}" aparece en ${matches} de ${totalCats} categorías.`
      };
    }
    return null;
  }

  private buildStep5Finding(step5: any, hasStep5: boolean): DiagnosticFinding | null {
    if (!hasStep5) {
      return null;
    }
    const noStock = step5.products_without_stock || [];
    const total = step5.total_products || 0;
    if (total <= 0) {
      return null;
    }
    if (noStock.length === total) {
      return { severity: 'critical', text: `Ninguno de los ${total} productos tiene stock en este store type.` };
    }
    if (noStock.length > 0) {
      return { severity: 'warning', text: `${noStock.length} de ${total} productos no tienen stock en este store type.` };
    }
    return { severity: 'ok', text: `Los ${total} productos de la campaña tienen stock en este store type.` };
  }

  private buildStep6Findings(step6: any, hasStep3: boolean): DiagnosticFinding[] {
    const results: DiagnosticFinding[] = [];
    const fr = step6.overall_fill_rate;
    const planned = step6.total_renders_planned || 0;
    const delivered = step6.total_renders_delivered || 0;

    if ('overall_fill_rate' in step6) {
      if (planned === 0) {
        results.push({ severity: 'critical', text: 'No hay renders planificados hoy para esta campaña.' });
      } else if (fr === 0) {
        results.push({
          severity: 'critical',
          text: `Fill rate global de 0% — se planificaron ${this.formatNumber(planned)} renders pero no se entregó ninguno.`
        });
      } else if (fr !== null && fr !== undefined && fr >= 1) {
        results.push({
          severity: 'warning',
          text: `Fill rate ${(fr * 100).toFixed(1)}% — se entregaron ${this.formatNumber(delivered)} renders de ${this.formatNumber(planned)} planificados (inventario agotado).`
        });
      } else if (fr !== null && fr !== undefined && fr < 0.1) {
        results.push({
          severity: 'warning',
          text: `Fill rate global muy bajo: ${(fr * 100).toFixed(1)}% (${this.formatNumber(delivered)} de ${this.formatNumber(planned)} renders).`
        });
      } else if (fr !== null && fr !== undefined) {
        results.push({
          severity: 'ok',
          text: `Fill rate global ${(fr * 100).toFixed(1)}% — ${this.formatNumber(delivered)} de ${this.formatNumber(planned)} renders entregados.`
        });
      }
    }

    const summary = step6.summary || [];
    const noDelivery = summary.filter((s: any) => s.fill_status === 'no_delivery');
    if (hasStep3 && summary.length > 0 && noDelivery.length > 0) {
      results.push({
        severity: noDelivery.length === summary.length ? 'critical' : 'warning',
        text: `${noDelivery.length} de ${summary.length} combinaciones (placement × store type) no entregaron impresiones.`
      });
    }

    return results;
  }

  private buildFindings(details: DiagnosticDetails): DiagnosticFinding[] {
    const findings: DiagnosticFinding[] = [];
    const step0 = details?.step0 || {} as any;
    const step1 = details?.step1 || {} as any;
    const step2 = details?.step2 || {} as any;
    const step6 = details?.step6 || {} as any;
    const step3 = details?.step3 || {} as any;
    const step4 = details?.step4 || {} as any;
    const step5 = details?.step5 || {} as any;
    const hasStep3 = 'step3' in details;
    const hasStep4 = 'step4' in details;
    const hasStep5 = 'step5' in details;

    const step0Finding = this.buildStep0Finding(step0);
    if (step0Finding) {
      findings.push(step0Finding);
    }

    const step1Finding = this.buildStep1Finding(step1);
    if (step1Finding) {
      findings.push(step1Finding);
    }

    const step2Finding = this.buildStep2Finding(step2);
    if (step2Finding) {
      findings.push(step2Finding);
    }

    const step3Finding = this.buildStep3Finding(step3, hasStep3);
    if (step3Finding) {
      findings.push(step3Finding);
    }

    const step4Finding = this.buildStep4Finding(step4, hasStep4);
    if (step4Finding) {
      findings.push(step4Finding);
    }

    const step5Finding = this.buildStep5Finding(step5, hasStep5);
    if (step5Finding) {
      findings.push(step5Finding);
    }

    findings.push(...this.buildStep6Findings(step6, hasStep3));

    const order: Record<string, number> = { critical: 0, warning: 1, ok: 2 };
    findings.sort((a, b) => order[a.severity] - order[b.severity]);
    return findings;
  }
}
